import React, { createContext, useContext, useState, useCallback } from 'react'

const LS_BOOKMARKS   = 'mimic_bookmarks'
const LS_SHOW_ONLY   = 'mimic_show_only_bookmarked'

export interface Bookmark {
  mimicId: string
  namespace: string
}

interface BookmarksCtx {
  bookmarks: Bookmark[]
  isBookmarked: (mimicId: string) => boolean
  toggle: (mimicId: string, namespace: string) => void
  showOnlyBookmarked: boolean
  setShowOnlyBookmarked: (v: boolean) => void
}

const Ctx = createContext<BookmarksCtx>({} as BookmarksCtx)

function loadBookmarks(): Bookmark[] {
  try { return JSON.parse(localStorage.getItem(LS_BOOKMARKS) || '[]') } catch { return [] }
}

export function BookmarksProvider({ children }: { children: React.ReactNode }) {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>(loadBookmarks)
  const [showOnlyBookmarked, setShowOnlyState] = useState<boolean>(
    () => localStorage.getItem(LS_SHOW_ONLY) === 'true'
  )

  const isBookmarked = useCallback(
    (mimicId: string) => bookmarks.some((b) => b.mimicId === mimicId),
    [bookmarks]
  )

  const toggle = useCallback((mimicId: string, namespace: string) => {
    setBookmarks((prev) => {
      const next = prev.some((b) => b.mimicId === mimicId)
        ? prev.filter((b) => b.mimicId !== mimicId)
        : [...prev, { mimicId, namespace }]
      localStorage.setItem(LS_BOOKMARKS, JSON.stringify(next))
      return next
    })
  }, [])

  const setShowOnlyBookmarked = useCallback((v: boolean) => {
    localStorage.setItem(LS_SHOW_ONLY, String(v))
    setShowOnlyState(v)
  }, [])

  return (
    <Ctx.Provider value={{ bookmarks, isBookmarked, toggle, showOnlyBookmarked, setShowOnlyBookmarked }}>
      {children}
    </Ctx.Provider>
  )
}

export const useBookmarks = () => useContext(Ctx)
